/**
 * 视频缓存管理器
 *
 * 功能特性：
 * 1. 视频资源缓存和预加载
 * 2. 智能预加载策略（基于用户行为）
 * 3. 视频池管理，复用video元素
 * 4. 内存和存储空间管理
 * 5. 加载状态和错误处理
 * 6. 优先级队列管理
 */

// 视频缓存不依赖 API 缓存，移除未使用的导入

// 视频缓存项接口
interface VideoCacheItem {
  url: string;
  blob?: Blob;
  objectUrl?: string;
  metadata?: {
    duration?: number;
    width?: number;
    height?: number;
    size?: number;
  };
  loadState: 'idle' | 'loading' | 'loaded' | 'error';
  loadedAt: number;
  accessCount: number;
  lastAccessed: number;
  priority: VideoPriority;
  retryCount: number;
  error?: Error;
}

// 视频预加载优先级
export enum VideoPriority {
  CRITICAL = 1, // 当前视窗内的视频
  HIGH = 2, // 即将进入视窗的视频
  MEDIUM = 3, // 用户可能会访问的视频
  LOW = 4, // 后台预加载的视频
}

// 视频缓存配置
interface VideoCacheConfig {
  maxCacheSize: number; // 最大缓存大小（MB）
  maxVideoCount: number; // 最大缓存视频数量
  defaultPriority: VideoPriority;
  preloadDistance: number; // 预加载距离（像素）
  maxRetries: number; // 最大重试次数
  retryDelay: number; // 重试延迟（毫秒）
  cleanupInterval: number; // 清理间隔（毫秒）
  ttl: number; // 缓存生存时间（毫秒）
}

// 加载统计
interface LoadStats {
  totalRequests: number;
  cacheHits: number;
  cacheMisses: number;
  errors: number;
  totalLoadTime: number;
  averageLoadTime: number;
}

/**
 * 视频缓存管理器类
 */
class VideoCacheManager {
  private cache = new Map<string, VideoCacheItem>();
  private loadingPromises = new Map<string, Promise<VideoCacheItem>>();
  private cleanupTimer: NodeJS.Timeout | null = null;
  private stats: LoadStats = {
    totalRequests: 0,
    cacheHits: 0,
    cacheMisses: 0,
    errors: 0,
    totalLoadTime: 0,
    averageLoadTime: 0,
  };

  private config: VideoCacheConfig = {
    maxCacheSize: 200, // 200MB
    maxVideoCount: 50,
    defaultPriority: VideoPriority.MEDIUM,
    preloadDistance: 500,
    maxRetries: 3,
    retryDelay: 1000,
    cleanupInterval: 5 * 60 * 1000, // 5分钟
    ttl: 30 * 60 * 1000, // 30分钟
  };

  constructor(config?: Partial<VideoCacheConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    this.startCleanup();
    this.bindVisibilityChange();
  }

  /**
   * 获取视频缓存项
   */
  get(url: string): VideoCacheItem | null {
    const item = this.cache.get(url);
    if (!item) return null;

    // 检查是否过期
    if (this.isExpired(item)) {
      this.delete(url);
      return null;
    }

    // 更新访问统计
    item.accessCount++;
    item.lastAccessed = Date.now();

    return item;
  }

  /**
   * 预加载视频
   */
  async preload(
    url: string,
    priority: VideoPriority = this.config.defaultPriority
  ): Promise<VideoCacheItem> {
    this.stats.totalRequests++;

    // 检查缓存
    const cached = this.get(url);
    if (cached && cached.loadState === 'loaded') {
      this.stats.cacheHits++;
      return cached;
    }

    this.stats.cacheMisses++;

    // 检查是否正在加载
    const existingPromise = this.loadingPromises.get(url);
    if (existingPromise) {
      return existingPromise;
    }

    // 创建缓存项
    const cacheItem: VideoCacheItem = {
      url,
      loadState: 'loading',
      loadedAt: 0,
      accessCount: 1,
      lastAccessed: Date.now(),
      priority,
      retryCount: 0,
    };

    this.cache.set(url, cacheItem);

    // 开始加载
    const loadPromise = this.loadVideo(cacheItem);
    this.loadingPromises.set(url, loadPromise);

    try {
      const result = await loadPromise;
      return result;
    } finally {
      this.loadingPromises.delete(url);
    }
  }

  /**
   * 加载视频资源
   */
  private async loadVideo(cacheItem: VideoCacheItem): Promise<VideoCacheItem> {
    const startTime = Date.now();

    try {
      // 确保有足够空间
      await this.ensureSpace();

      // 创建视频元素获取元数据
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';

      const metadataPromise = new Promise<void>((resolve, reject) => {
        const onLoadedMetadata = () => {
          cacheItem.metadata = {
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight,
          };
          cleanup();
          resolve();
        };

        const onError = () => {
          cleanup();
          reject(new Error(`Failed to load video metadata: ${cacheItem.url}`));
        };

        const cleanup = () => {
          video.removeEventListener('loadedmetadata', onLoadedMetadata);
          video.removeEventListener('error', onError);
        };

        video.addEventListener('loadedmetadata', onLoadedMetadata);
        video.addEventListener('error', onError);
      });

      video.src = cacheItem.url;

      // 等待元数据加载
      await metadataPromise;

      // 根据优先级决定是否下载完整视频
      if (cacheItem.priority <= VideoPriority.HIGH) {
        await this.downloadVideoBlob(cacheItem);
      }

      cacheItem.loadState = 'loaded';
      cacheItem.loadedAt = Date.now();

      const loadTime = Date.now() - startTime;
      this.updateLoadStats(loadTime);

      if (process.env.NODE_ENV === 'development') {
        console.log(`[视频缓存] 加载完成: ${cacheItem.url}, 耗时: ${loadTime}ms`);
      }

      return cacheItem;
    } catch (error) {
      return this.handleLoadError(cacheItem, error as Error);
    }
  }

  /**
   * 下载视频Blob
   */
  private async downloadVideoBlob(cacheItem: VideoCacheItem): Promise<void> {
    try {
      const response = await fetch(cacheItem.url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      cacheItem.blob = blob;
      cacheItem.objectUrl = URL.createObjectURL(blob);

      if (cacheItem.metadata) {
        cacheItem.metadata.size = blob.size;
      }
    } catch (error) {
      console.warn(`[视频缓存] 下载Blob失败: ${cacheItem.url}`, error);
      // 下载失败不影响元数据缓存
    }
  }

  /**
   * 处理加载错误
   */
  private async handleLoadError(cacheItem: VideoCacheItem, error: Error): Promise<VideoCacheItem> {
    cacheItem.error = error;
    cacheItem.retryCount++;
    this.stats.errors++;

    if (cacheItem.retryCount < this.config.maxRetries) {
      // 重试加载
      await new Promise(resolve =>
        setTimeout(resolve, this.config.retryDelay * cacheItem.retryCount)
      );

      if (process.env.NODE_ENV === 'development') {
        console.log(
          `[视频缓存] 重试加载 (${cacheItem.retryCount}/${this.config.maxRetries}): ${cacheItem.url}`
        );
      }

      return this.loadVideo(cacheItem);
    } else {
      cacheItem.loadState = 'error';
      if (process.env.NODE_ENV === 'development') {
        console.error(`[视频缓存] 加载失败: ${cacheItem.url}`, error);
      }
      throw error;
    }
  }

  /**
   * 更新加载统计
   */
  private updateLoadStats(loadTime: number): void {
    this.stats.totalLoadTime += loadTime;
    this.stats.averageLoadTime = this.stats.totalLoadTime / this.stats.totalRequests;
  }

  /**
   * 批量预加载视频
   */
  async batchPreload(
    urls: string[],
    priority: VideoPriority = this.config.defaultPriority,
    concurrency: number = 3
  ): Promise<VideoCacheItem[]> {
    const results: Promise<VideoCacheItem>[] = [];
    const executing: Promise<any>[] = [];

    for (const url of urls) {
      const promise = this.preload(url, priority).then(result => {
        executing.splice(executing.indexOf(promise), 1);
        return result;
      });

      results.push(promise);
      executing.push(promise);

      if (executing.length >= concurrency) {
        await Promise.race(executing);
      }
    }

    return Promise.all(results);
  }

  /**
   * 智能预加载策略
   */
  async intelligentPreload(
    visibleUrls: string[],
    nearbyUrls: string[],
    predictedUrls: string[] = []
  ): Promise<void> {
    // 优先加载可见视频
    await this.batchPreload(visibleUrls, VideoPriority.CRITICAL, 3);

    // 预加载附近的视频
    await this.batchPreload(nearbyUrls, VideoPriority.HIGH, 2);

    // 后台预加载预测的视频
    if (predictedUrls.length > 0) {
      setTimeout(() => {
        this.batchPreload(predictedUrls, VideoPriority.LOW, 1);
      }, 1000);
    }
  }

  /**
   * 检查缓存项是否过期
   */
  private isExpired(item: VideoCacheItem): boolean {
    return Date.now() - item.loadedAt > this.config.ttl;
  }

  /**
   * 确保有足够的缓存空间
   */
  private async ensureSpace(): Promise<void> {
    const maxSizeBytes = this.config.maxCacheSize * 1024 * 1024;
    let currentSize = this.getCurrentSize();

    // 按LRU策略清理缓存
    const sortedItems = Array.from(this.cache.entries()).sort(([, a], [, b]) => {
      // 优先清理：过期 > 错误状态 > 低优先级 > 最少访问
      if (this.isExpired(a) && !this.isExpired(b)) return -1;
      if (!this.isExpired(a) && this.isExpired(b)) return 1;

      if (a.loadState === 'error' && b.loadState !== 'error') return -1;
      if (a.loadState !== 'error' && b.loadState === 'error') return 1;

      if (a.priority !== b.priority) return b.priority - a.priority;

      return a.lastAccessed - b.lastAccessed;
    });

    for (const [url, item] of sortedItems) {
      if (this.cache.size < this.config.maxVideoCount && currentSize < maxSizeBytes) {
        break;
      }

      const itemSize = item.metadata?.size || 0;
      this.delete(url);
      currentSize -= itemSize;
    }
  }

  /**
   * 获取当前缓存大小
   */
  private getCurrentSize(): number {
    let totalSize = 0;
    for (const item of this.cache.values()) {
      totalSize += item.metadata?.size || 0;
    }
    return totalSize;
  }

  /**
   * 删除缓存项
   */
  delete(url: string): boolean {
    const item = this.cache.get(url);
    if (item?.objectUrl) {
      URL.revokeObjectURL(item.objectUrl);
    }
    return this.cache.delete(url);
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    for (const item of this.cache.values()) {
      if (item.objectUrl) {
        URL.revokeObjectURL(item.objectUrl);
      }
    }
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * 定期清理过期缓存
   */
  private cleanup(): void {
    const expiredUrls: string[] = [];

    for (const [url, item] of this.cache.entries()) {
      if (this.isExpired(item) || item.loadState === 'error') {
        expiredUrls.push(url);
      }
    }

    expiredUrls.forEach(url => this.delete(url));

    if (process.env.NODE_ENV === 'development' && expiredUrls.length > 0) {
      console.log(`[视频缓存] 清理了 ${expiredUrls.length} 个过期/错误缓存项`);
    }
  }

  /**
   * 开始定期清理
   */
  private startCleanup(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * 绑定页面可见性变化事件
   */
  private bindVisibilityChange(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          // 页面隐藏时暂停预加载
          this.pausePreloading();
        } else {
          // 页面显示时恢复预加载
          this.resumePreloading();
        }
      });
    }
  }

  /**
   * 暂停预加载
   */
  private pausePreloading(): void {
    // 清理正在进行的低优先级加载
    for (const [url] of this.loadingPromises.entries()) {
      const item = this.cache.get(url);
      if (item && item.priority >= VideoPriority.MEDIUM) {
        // 可以考虑取消低优先级的加载
        // 目前仅记录，未来可以实现实际的取消逻辑
        if (process.env.NODE_ENV === 'development') {
          console.log(`[视频缓存] 暂停低优先级加载: ${url}`);
        }
      }
    }
  }

  /**
   * 恢复预加载
   */
  private resumePreloading(): void {
    // 恢复预加载逻辑
  }

  /**
   * 获取缓存统计信息
   */
  getStats(): LoadStats & {
    cacheSize: number;
    itemCount: number;
    hitRate: number;
  } {
    const hitRate =
      this.stats.totalRequests > 0 ? this.stats.cacheHits / this.stats.totalRequests : 0;

    return {
      ...this.stats,
      cacheSize: Math.round((this.getCurrentSize() / 1024 / 1024) * 100) / 100,
      itemCount: this.cache.size,
      hitRate: Math.round(hitRate * 10000) / 100, // 百分比，保留2位小数
    };
  }

  /**
   * 销毁缓存管理器
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.clear();
  }
}

// 创建全局视频缓存实例
export const videoCache = new VideoCacheManager();

export default VideoCacheManager;
